import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { EntityProps, EntityState, setAllEntities, setEntity, updateEntity } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Référentiel } from '../models/référentiel.model';
import { ReferentielCreateRequest } from '../request/create-request';
import { ReferentielUpdateRequest } from '../request/update-request';
import { RéférentielService } from '../référentiel.service';
import { ReferentielState } from './referentiel.state';
import { SelectedEntityState } from '@shared/store/selected-entity.feature';
import { FinParcoursStore } from '../../../référentiel-métier-parcours/store/fin-parcours/fin-parcours.store';
import { Response } from '../../../shared/models/response.model';

export function avecMéthodesRéférentiels<_>() {
  return signalStoreFeature(
    {
      state: type<ReferentielState & EntityState<Référentiel> & SelectedEntityState>(),
      props: type<EntityProps<Référentiel>>(),
    },
    withMethods((store) => ({
      chargerRéférentiels: (state: { referentiels: Référentiel[] }) =>
        patchState(store, setAllEntities(state.referentiels)),
      modifierRéférentiel: (state: { id: string; référentiel: Partial<Référentiel> }) =>
        patchState(store, updateEntity({ id: state.id, changes: state.référentiel })),
      ajouterRéférentiel: (state: { référentiel: Référentiel }) => patchState(store, setEntity(state.référentiel)),
      reinitialiserFiltres: () => patchState(store, { filtres: {} }),
    })),
    withMethods((store, service = inject(RéférentielService), finParcoursStore = inject(FinParcoursStore)) => ({
      charger: rxMethod<void>(
        pipe(
          switchMap(() =>
            service.findAll().pipe(
              tapResponse({
                next: (result: Response<Référentiel>) => {
                  patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
                  store.chargerRéférentiels({
                    referentiels: result.items,
                  });
                },
                error: () => {
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Un problème est survenu lors du chargement des référentiels, contactez le support.',
                      titre: "Le chargement des cartographies n'a pas abouti",
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      chargerParCartographie: rxMethod<string>(
        pipe(
          switchMap((idCartographie) =>
            service.findAll(idCartographie).pipe(
              tapResponse({
                next: (result: Response<Référentiel>) => {
                  patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
                  store.chargerRéférentiels({
                    referentiels: result.items,
                  });
                },
                error: () => {
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Un problème est survenu lors du chargement des référentiels, contactez le support.',
                      titre: "Le chargement des cartographies n'a pas abouti",
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      selectionnerParId: rxMethod<string>(
        pipe(
          switchMap((idRéférentiel) =>
            service.findById(idRéférentiel).pipe(
              tapResponse({
                next: (result) => {
                  store.modifierRéférentiel({ id: idRéférentiel, référentiel: result });
                  finParcoursStore.chargerFinsParcours({ fins: result.finsParcours });
                },
                error: (err) => console.error(err),
              }),
            ),
          ),
        ),
      ),
      créer: rxMethod<{ data: ReferentielCreateRequest; next: () => void }>(
        pipe(
          switchMap(({ data, next }) =>
            service.create(data).pipe(
              tapResponse({
                next: (référentiel) => {
                  store.ajouterRéférentiel({
                    référentiel,
                  });
                  next();
                },
                error: (err) => {
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la création d'un référentiel, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        id: string;
        data: ReferentielUpdateRequest;
        next?: () => void;
        error?: () => void;
      }>(
        pipe(
          switchMap(({ id, data, next, error }) =>
            service.update(id, data).pipe(
              tapResponse({
                next: () => {
                  store.modifierRéférentiel({
                    id,
                    référentiel: {
                      ...data,
                      id,
                    },
                  });
                  next?.();
                },
                error: (err) => {
                  const toastrId = uuidv7();

                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de la modification d'un référentiel, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  console.error(err);
                  error?.();
                },
              }),
            ),
          ),
        ),
      ),
      exportReferentiel: rxMethod<{
        idReferentiel: string;
        next: (blob: Blob) => void;
        error: () => void;
      }>(
        pipe(
          switchMap(({ idReferentiel, next, error }) => {
            return service.export(idReferentiel).pipe(
              tapResponse({
                next: (blob: Blob) => {
                  next(blob);
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: `Un problème est survenu lors de l'export du référentiel, contactez le support.`,
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            );
          }),
        ),
      ),
      select: (référentiel: Référentiel) => {
        patchState(store, setEntity(référentiel));
        patchState(store, { selectedEntityId: référentiel.id });
      },
      // Tri
      setTri: (nom: string, direction: 'asc' | 'desc') => {
        patchState(store, {
          sort: {
            nom,
            direction: !direction || direction === 'asc' ? '+' : '-',
          },
        });
        // Reset pagination when sort order changes
        patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
      },
      // Filtres
      setFiltres: (filtres: { equipeResponsable?: string; idMetier?: string; publicCible?: string[] }) => {
        patchState(store, {
          filtres: {
            equipeResponsable: filtres.equipeResponsable,
            idMetier: filtres.idMetier,
            publicsCibles: filtres.publicCible,
          },
        });
        // Reset pagination when filter changes
        patchState(store, { paginator: { ...store.paginator(), pageNb: 1 } });
      },
    })),
  );
}
